/*eslint max-lines-per-function: ["error", 460]*/
/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Role } from '../../../common/spdCore/authorization/roles';
import momencik from '../../../common/momencik';
import HealthcareFacilitySelector from '../../components/Selects/HealthcareFacilitySelector';
import UserSelector from '../../components/Selects/UserSelector';
import { useAuth } from '../../../common/hooks/useAuth';
import SpinnerButton from '../../../common/components/Button';
import FormRow from '../../../app/components/FormRow';
import RegisterSelector from '../../components/Selects/RegisterSelector';
import ArticlesMultiSelector from '../../components/Selects/ArticlesMultiSelector';
import { ReactSelectOption } from '../../components/Selects/SortableSelect';
import CompanyByNameOrNipSelector from '../../components/Company/CompanyByNameOrNipSelector';

export type FilterReportData = {
  dateFrom?: Date;
  dateTo?: Date;
  companyId?: string;
  healthcareFacilityId?: string;
  withExamined?: boolean;
  judgments?: boolean;
  cards?: boolean;
  consultations?: boolean;
  attachments?: boolean;
  psychologistUId?: string;
  registerId?: string;
  articlesIds?: string[];
  withLinkedPayments?: boolean;
  onlyUnpaid?: boolean;
};

interface FilterProps {
  roles: Role[];
  withUser: boolean;
  withDate: boolean;
  withEndStartDate?: boolean;
  withHealthcareFacility: boolean;
  withCompany?: boolean;
  withOnlyMonthlySettlement?: boolean;
  withDocumentsKind?: boolean;
  withRegister?: boolean;
  withArticles?: boolean;
  withLinkedPayments?: boolean;
  withOnlyUnpaid?: boolean;
  withExamined?: boolean;
  userUid?: string | undefined;
  headerName?: string;
  userName?: string;
  canClean?: boolean;
  requiredCompany?: boolean;
  emptyOnStart?: boolean;
  dateFromText?: string;
  dateToText?: string;
  dateFrom?: Date;
  onChangeDateFrom?: (dateFrom: Date | undefined) => void;
  onChangeDateTo?: (dateTo: Date | undefined) => void;
  onChangeUserUid?: (userUid: string | undefined) => void;
  onChangeCompanyId?: (userUid: string | undefined) => void;
  onChangeHealthcareFacilityId?: (
    healthcareFacilityId: string | undefined,
  ) => void;
  onChangeOnlyMonthlySettlement?: (onlyMonthlySettlement: boolean) => void;
  onExport?: (
    dateFrom: Date | undefined,
    dateTo: Date | undefined,
    userUid: string | undefined,
    healthcareFacilityId: string | undefined,
  ) => void;
  onSearch?: (filter: FilterReportData) => void;
}

export const Filter: React.FC<FilterProps> = props => {
  const spdApi = useSpdCore();
  const auth = useAuth();
  const [showNewAlert, setShowNewAlert] = useState(false);
  const startDate = new Date(
    (props.dateFrom || new Date()).getFullYear(),
    (props.dateFrom || new Date()).getMonth(),
    props.dateFrom ? props.dateFrom.getDate() : 1,
    props.withEndStartDate ? 23 : 0,
    props.withEndStartDate ? 59 : 0,
    props.withEndStartDate ? 59 : 0,
    0,
  );
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    new Date(startDate),
  );
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [userUid, setUserUid] = useState<string | undefined>(props.userUid);
  const [healthcareFacilityId, setHealthcareFacilityId] = useState<
    string | undefined
  >();
  const [registerId, setRegisterId] = useState<string | undefined>();
  const [articles, setArticles] = useState<ReactSelectOption[]>();
  const [onlyMonthlySettlement, setOnlyMonthlySettlement] = useState<
    boolean | undefined
  >(false);
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [judgments, setJudgments] = useState(true);
  const [cards, setCards] = useState(true);
  const [consultations, setConsultations] = useState(true);
  const [attachments, setAttachments] = useState(true);
  const [withLinkedPayments, setWithLinkedPayments] = useState(false);
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [withExamined, setWithExamined] = useState(false);

  useEffect(() => {
    if (!healthcareFacilityId && !props.emptyOnStart) {
      spdApi.healthCareFacilities
        .getDefault()
        .then(hfId => {
          setHealthcareFacilityId(hfId);
          if (props.onChangeHealthcareFacilityId) {
            props.onChangeHealthcareFacilityId(hfId);
          }
        })
        .catch(error => {
          console.log('Brak dostępu do placówki głównej. ', error);
        });
    }
  }, [spdApi.healthCareFacilities, props, healthcareFacilityId]);

  useEffect(() => {
    if (!props.emptyOnStart && props.onChangeUserUid) {
      spdApi.users
        .getAllByRoles(props.roles)
        .then(us => {
          const userAuth = us.find(u => u.uid === auth.currentUser?.uid);
          if (props.withUser && !props.userUid) {
            if (userAuth) {
              setUserUid(userAuth.uid ? userAuth.uid : undefined);
              if (props.onChangeUserUid) {
                props.onChangeUserUid(userAuth.uid || undefined);
              }
            } else {
              setUserUid(us && us[0].uid ? us[0].uid : undefined);
              if (props.onChangeUserUid) {
                props.onChangeUserUid(us && us[0].uid ? us[0].uid : undefined);
              }
            }
          }
        })
        .catch(error => {
          console.log('Nie udało się pobrać pracowników: ', error);
        });
    }
  }, [spdApi.users, auth.currentUser?.uid, props]);

  const handleClear = () => {
    setUserUid(undefined);

    setDateFrom(new Date(startDate));
    setDateTo(undefined);
    setHealthcareFacilityId(undefined);
    setRegisterId(undefined);
    setArticles([]);
    setCompanyId(undefined);
    setShowNewAlert(false);
    setOnlyMonthlySettlement(false);
    setJudgments(true);
    setCards(true);
    setConsultations(true);
    setAttachments(true);
    setWithLinkedPayments(false);
    setOnlyUnpaid(false);
    setWithExamined(false);
  };

  const handleSearch = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setShowNewAlert(true);
      return;
    }
    setShowNewAlert(false);

    if (props.onChangeDateFrom) {
      props.onChangeDateFrom(dateFrom);
    }
    if (props.onChangeDateTo) {
      props.onChangeDateTo(dateTo);
    }
    if (props.onChangeHealthcareFacilityId) {
      props.onChangeHealthcareFacilityId(healthcareFacilityId);
    }
    if (props.withUser && props.onChangeUserUid) {
      props.onChangeUserUid(
        userUid === '' || userUid === null ? undefined : userUid,
      );
    }
    if (props.withCompany && props.onChangeCompanyId) {
      props.onChangeCompanyId(companyId);
    }

    if (
      props.withOnlyMonthlySettlement &&
      props.onChangeOnlyMonthlySettlement
    ) {
      props.onChangeOnlyMonthlySettlement(onlyMonthlySettlement || false);
    }
    const dateToEndOfDay = dateTo
      ? new Date(
          dateTo.getFullYear(),
          dateTo.getMonth(),
          dateTo.getDate(),
          23,
          59,
          59,
        )
      : undefined;
    if (props.onSearch) {
      props.onSearch({
        dateFrom,
        dateTo: dateToEndOfDay,
        companyId,
        healthcareFacilityId,
        judgments,
        cards,
        consultations,
        attachments,
        psychologistUId: userUid,
        registerId,
        articlesIds: articles?.map(o => o.value),
        withLinkedPayments,
        onlyUnpaid,
        withExamined: props.withExamined ? withExamined : undefined,
      });
    }
  };

  const handleExport = async () => {
    setIsLoadingExport(true);
    const dateToEndOfDay = dateTo
      ? new Date(
          dateTo.getFullYear(),
          dateTo.getMonth(),
          dateTo.getDate(),
          23,
          59,
          59,
        )
      : undefined;
    if (props.onExport) {
      await props.onExport(
        dateFrom,
        dateToEndOfDay,
        userUid,
        healthcareFacilityId,
      );
    }
    setIsLoadingExport(false);
  };

  return (
    <Card className="mb-2">
      {props.headerName && <Card.Header>{props.headerName}</Card.Header>}
      <Card.Body>
        <Form className="d-grid gap-3">
          {props.withUser && (
            <Row>
              <Form.Group as={Row} controlId="userName" readOnly={true}>
                <Form.Label column sm="3">
                  {props.userName}
                </Form.Label>
                <Col sm="9">
                  <UserSelector
                    readOnly={!auth?.check([Role.Admin, Role.Office])}
                    value={userUid || ''}
                    roles={props.roles}
                    onChange={option => {
                      setUserUid(option.value || undefined);
                    }}
                    clearable={props.canClean}
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withOnlyMonthlySettlement && (
            <Row>
              <Form.Group as={Row} controlId="monthlySettlement">
                <Form.Label column sm="3">
                  Tylko firmy rozliczane miesięcznie
                </Form.Label>
                <Col sm="9">
                  <Form.Check
                    type="checkbox"
                    checked={onlyMonthlySettlement || false}
                    onChange={e => {
                      if (e.target.checked) {
                        setCompanyId(undefined);
                      }
                      setOnlyMonthlySettlement(e.target.checked);
                    }}
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withCompany && (
            <Row>
              <Form.Group as={Row} controlId="company">
                <Form.Label column sm="3">
                  Firma
                </Form.Label>
                <Col sm="9">
                  <CompanyByNameOrNipSelector
                    value={companyId}
                    onChange={e => setCompanyId(e.value || undefined)}
                    clearable={!props.requiredCompany}
                    readOnly={onlyMonthlySettlement || false}
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withHealthcareFacility && (
            <Row>
              <Form.Group as={Row} controlId="healthcareFacility">
                <Form.Label column sm="3">
                  Placówka
                </Form.Label>
                <Col sm="9">
                  <HealthcareFacilitySelector
                    value={healthcareFacilityId || ''}
                    onChange={option => {
                      setHealthcareFacilityId(option.value || undefined);
                    }}
                    clearable={props.canClean}
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withRegister && (
            <Row>
              <Form.Group as={Row} controlId="register">
                <Form.Label column sm="3">
                  Rejestr
                </Form.Label>
                <Col sm="9">
                  <RegisterSelector
                    value={registerId || ''}
                    onChange={option => {
                      setRegisterId(option.value || undefined);
                    }}
                    clearable={props.canClean}
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withArticles && (
            <Row>
              <Form.Group as={Row} controlId="articles">
                <Form.Label column sm="3">
                  Artykuły
                </Form.Label>
                <Col sm="9">
                  <ArticlesMultiSelector
                    selectedArticles={articles}
                    onChange={options =>
                      setArticles((options || []).map(o => o))
                    }
                  />
                </Col>
              </Form.Group>
            </Row>
          )}
          {props.withDate && (
            <>
              <Row>
                <Form.Group as={Row} controlId="dateFrom">
                  <Form.Label column sm="3">
                    {props.dateFromText || 'Data od'}
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      type="date"
                      name="datepic"
                      placeholder="DateRange"
                      value={momencik(dateFrom, 'YYYY-MM-DD')}
                      onChange={e =>
                        setDateFrom(
                          e.target.value ? new Date(e.target.value) : undefined,
                        )
                      }
                    />
                  </Col>
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Row} controlId="dateTo">
                  <Form.Label column sm="3">
                    {props.dateToText || 'Data do'}
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      type="date"
                      name="datepic"
                      placeholder="DateRange"
                      value={momencik(dateTo, 'YYYY-MM-DD')}
                      onChange={e =>
                        setDateTo(
                          e.target.value ? new Date(e.target.value) : undefined,
                        )
                      }
                    />
                  </Col>
                </Form.Group>
              </Row>
              {props.withDocumentsKind && (
                <>
                  <FormRow controlId="judgments" label="Orzeczenia">
                    <Form.Check
                      type="checkbox"
                      checked={judgments ? true : false}
                      onChange={e => setJudgments(e.target.checked)}
                    />
                  </FormRow>
                  <FormRow controlId="cards" label="Karty">
                    <Form.Check
                      type="checkbox"
                      checked={cards ? true : false}
                      onChange={e => setCards(e.target.checked)}
                    />
                  </FormRow>
                  <FormRow controlId="consultations" label="Konsultacje">
                    <Form.Check
                      type="checkbox"
                      checked={consultations ? true : false}
                      onChange={e => setConsultations(e.target.checked)}
                    />
                  </FormRow>
                  <FormRow controlId="attachments" label="Załączniki">
                    <Form.Check
                      type="checkbox"
                      checked={attachments ? true : false}
                      onChange={e => setAttachments(e.target.checked)}
                    />
                  </FormRow>
                </>
              )}
            </>
          )}
          {props.withLinkedPayments && (
            <FormRow
              controlId="withLinkedPayments"
              label="Dodaj informację o dowiązanych wpłatach"
            >
              <Form.Check
                type="checkbox"
                checked={withLinkedPayments}
                onChange={e => setWithLinkedPayments(e.target.checked)}
              />
            </FormRow>
          )}
          {props.withOnlyUnpaid && (
            <FormRow controlId="onlyUnpaid" label="Pokaż tylko niezapłacone">
              <Form.Check
                type="checkbox"
                checked={onlyUnpaid}
                onChange={e => setOnlyUnpaid(e.target.checked)}
              />
            </FormRow>
          )}
          {props.withExamined && (
            <FormRow controlId="withExamined" label="Z podziałem na badanych">
              <Form.Check
                type="checkbox"
                checked={withExamined}
                onChange={e => setWithExamined(e.target.checked)}
              />
            </FormRow>
          )}
        </Form>
        {showNewAlert && (
          <Alert variant="danger" className="mt-3">
            Zły zakres dat
          </Alert>
        )}
        <div className="d-flex justify-content-end mt-3">
          <Button className="me-2" variant="outline-dark" onClick={handleClear}>
            Wyczyść
          </Button>
          <Button
            className="me-2"
            variant="outline-primary"
            onClick={handleSearch}
          >
            Szukaj
          </Button>
          {props.onExport && (
            <SpinnerButton
              variant="outline-primary"
              onClick={handleExport}
              isLoading={isLoadingExport}
            >
              Eksportuj
            </SpinnerButton>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};
